import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useStateFromSearchParams } from '@/hooks/useStateFromSearchParams';
import { ExampleSchemaWithRunsAndOptionalFields } from '@/types/schema';

interface UseRepetitionsSidePaneProps {
  detailExample?: ExampleSchemaWithRunsAndOptionalFields | null;
  setDetailRunId: (runId: string | null) => void;
  setSearchParams: ReturnType<typeof useSearchParams>[1];
}

interface RepetitionsSidePaneInfo {
  session: string;
  example: string;
}

export function useRepetitionsSidePane({
  detailExample,
  setDetailRunId,
  setSearchParams,
}: UseRepetitionsSidePaneProps) {
  const [repetitionsSidePaneInfoRaw, setRepetitionsSidePaneInfoRaw] =
    useStateFromSearchParams('repsDetailSidePane', null, setSearchParams);

  const repetitionsSidePaneItems = useMemo(() => {
    return repetitionsSidePaneInfoRaw
      ? repetitionsSidePaneInfoRaw.split(',').filter(Boolean)
      : [];
  }, [repetitionsSidePaneInfoRaw]);

  const repetitionsSidePaneInfo = useMemo(() => {
    if (repetitionsSidePaneItems.length !== 2) {
      return null;
    }
    return {
      session: repetitionsSidePaneItems[0],
      example: repetitionsSidePaneItems[1],
    } as RepetitionsSidePaneInfo;
  }, [repetitionsSidePaneItems]);

  const onOpenRepetitionsSidebar = useCallback(
    (session: string | null) => {
      setRepetitionsSidePaneInfoRaw(
        session && detailExample ? `${session},${detailExample.id}` : null
      );
      if (session && detailExample) {
        setDetailRunId(null);
      }
    },
    [detailExample, setDetailRunId, setRepetitionsSidePaneInfoRaw]
  );

  useEffect(() => {
    onOpenRepetitionsSidebar(null);
  }, [detailExample?.id, onOpenRepetitionsSidebar]);

  return {
    repetitionsSidePaneInfo,
    onOpenRepetitionsSidebar,
  };
}
